/**
 * Закреплённые разделы сайдбара.
 *
 * Кассир/менеджер часто работает в 1–2 разделах (например «Каталог →
 * События») и не хочет каждый раз раскрывать SubMenu. Пин выносит лист
 * в отдельную секцию наверху меню.
 *
 * Хранение — localStorage, per-user: ключ `kassir:nav:pinned:<userId>`.
 * На кассовой машине за одним браузером работают разные смены, общий
 * ключ смешал бы их пины. BE-эндпоинта под пользовательские настройки
 * пока нет; когда появится — меняем только этот файл.
 *
 * Закрепляем ТОЛЬКО листья (`section/leaf`), не секции целиком:
 * секция без раскрытия бесполезна, а лист — прямая ссылка.
 */

import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';

/** Ключ листа в меню: `catalog/events`, `finance/tax` и т.п. */
export type PinnedKey = string;

/** Больше — сайдбар превращается в свалку, теряется смысл «быстрого». */
export const MAX_PINNED = 6;

const storageKey = (userId: number | string | null): string =>
  `kassir:nav:pinned:${userId ?? 'anon'}`;

/**
 * Текущий userId — ставится из `AppShell` при монтировании. Отдельный
 * atom, потому что ключ storage зависит от юзера, а `atomWithStorage`
 * ключ статичный: пересоздаём хранилище через `pinnedNavAtom` ниже.
 */
export const pinnedUserIdAtom = atom<number | string | null>(null);

/** Кэш atom'ов по userId — не пересоздаём при каждом рендере. */
const cache = new Map<string, ReturnType<typeof atomWithStorage<PinnedKey[]>>>();

const storageFor = (userId: number | string | null) => {
  const key = storageKey(userId);
  const existing = cache.get(key);
  if (existing) return existing;
  const created = atomWithStorage<PinnedKey[]>(key, [], undefined, {
    getOnInit: true,
  });
  cache.set(key, created);
  return created;
};

/** Список закреплённых ключей текущего юзера. */
export const pinnedNavAtom = atom(
  (get) => get(storageFor(get(pinnedUserIdAtom))),
  (get, set, next: PinnedKey[]) => {
    set(storageFor(get(pinnedUserIdAtom)), next.slice(0, MAX_PINNED));
  },
);

/** Toggle: был закреплён — убираем, не был — добавляем в конец. */
export const togglePinnedAtom = atom(null, (get, set, key: PinnedKey) => {
  const current = get(pinnedNavAtom);
  set(
    pinnedNavAtom,
    current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
  );
});
